// @flow
import {BatchProfile} from '@batch.com/react-native-plugin';
import {CommonActions} from '@react-navigation/native';
import {Platform} from 'react-native';
import {
  all,
  call,
  put,
  race,
  select,
  take,
  takeEvery,
} from 'redux-saga/effects';
import {clearUsages, fetchUsages} from '../../actions/usage';
import {tripica} from '../../api';
import requestCacheManager, {
  DASHBOARD_CACHE_DURATION,
} from '../../api/requestCacheManager';
import * as Analytics from '../../lib/analytics';
import HandleErrorService from '../../lib/handleError';
import Navigation from '../../lib/navigation';
import {fetchCustomerFailure, setCustomer} from '../Customer';
import {getDeliveryResources} from '../DeliveryResource/sagas';
import {setLoading} from '../LoadingStatus/actions';
import {initStateWithOfferSaga} from '../NewOffer/sagas';
import {fetchNextPlan} from '../NextPlan';
import {fetchPhoneLinesSuccess} from '../PhoneLines/actions';
import {saveAsFirstSubscription} from '../Subscription';
import {setCurrentSubscription} from '../Subscription/actions';
import {
  fetchAll as fetchAllSubscriptions,
  phoneLinesSelector,
} from '../Subscriptions';
import {selectSubscription, setHasRefreshedAtLeastOnce} from './actions';
import {
  currentSubscriptionNextRenewalDateSelector,
  currentSubscriptionSelector,
  isLineOFFSelector
} from './reducer';

import type {IOEffect} from 'redux-saga/effects';
import {createDeviceId} from '../Authentication/sagas';
import {deviceIdSelector} from '../Authentication/selectors';
import {
  fetchAvailableVouchersSuccess,
  setVoucherDetailsComeFrom,
} from '../Voucher';
import type {Action} from './actions';
import WizzeeLog from '../../lib/wizzeeLog';

export function* selectSubscriptionSaga(
  action: Action,
): Generator<IOEffect, void, any> {
  if (action.type !== 'SELECT_SUBSCRIPTION') return; // for flow
  try {
    const {subscription} = action.payload;
    if (!subscription) {
      return;
    }
    yield put(setCurrentSubscription(subscription));
    Analytics.setUserContext({ msisdn: subscription.line.number });
    const mustFetchUsages =
      subscription.status === 'ACTIVE' || subscription.status === 'SUSPENDED';

    yield all([
      put(fetchNextPlan(subscription)),
      mustFetchUsages
        ? put(fetchUsages(subscription.line.number))
        : put(clearUsages()),
    ]);

    if (!action.payload.skipSavingAsFirstSubscription) {
      yield put(saveAsFirstSubscription(subscription));
    }
  } catch (e) {
    WizzeeLog.warn('[saga] selectSubscriptionSaga', e);
    HandleErrorService.showToastError(e);
  }
}

// Need more tests !
export function* fetchDashboardDataSaga(action: Object): Generator<*, *, *> {
  yield put(setLoading('dashboard', true));
  try {
    if (action?.payload?.forceRefresh) {
      requestCacheManager.invalidate();
    }

    // Voucher fetch is non-critical: a failure must not prevent subscriptions from loading.
    try {
      const vouchers = yield call([tripica.api, 'fetchAvailableVouchers']);
      yield put(fetchAvailableVouchersSuccess(vouchers));
      yield put(setVoucherDetailsComeFrom('dashboard'));
    } catch (voucherError) {
      WizzeeLog.warn('[saga] fetchAvailableVouchers failed, continuing', voucherError);
    }

    yield put(fetchAllSubscriptions());
    yield race({
      success: take('SUBSCRIPTION_FETCH_ALL_SUCCESS'),
      error: take('SUBSCRIPTION_FETCH_ALL_FAILURE'),
    });

    const customer = yield call([tripica, 'getCustomer']);
    yield put(setCustomer(customer));

    // saving customerId into Batch backend, to identify user for push notifications
    BatchProfile.identify(customer.externalId);

    const phoneLines = yield select(phoneLinesSelector);
    yield put(fetchPhoneLinesSuccess(phoneLines));

    const deviceId = yield select(deviceIdSelector);
    if (!deviceId) {
      yield call(createDeviceId);
    }

    const currentSubscription = yield select(currentSubscriptionSelector);
    const currentSubscriptionNextRenewalDate = yield select(
      currentSubscriptionNextRenewalDateSelector,
    );
    const currentDate = Date.now();
    if (
      currentSubscriptionNextRenewalDate <= currentDate &&
      currentDate <=
      currentSubscriptionNextRenewalDate + DASHBOARD_CACHE_DURATION * 1000
    ) {
      requestCacheManager.invalidate();
    }

    yield put(selectSubscription(currentSubscription, true));    

    if (Platform.OS !== 'web') {
      yield call(getDeliveryResources);
    }
  } catch (e) {
    HandleErrorService.showToastError(e);
    yield put(fetchCustomerFailure(e));
    WizzeeLog.warn('[saga] fetchDashboardDataSaga', e);
  } finally {
    yield put(setLoading('dashboard', false));
    yield put(setHasRefreshedAtLeastOnce());
  }
}

export function* touchPlanFrameSaga(action: Action): Generator<*, *, *> {
  if (action.type !== 'TOUCH_PLAN_FRAME') return; // for flow
  const isLineOff = yield select(isLineOFFSelector);
  if (isLineOff) {
    yield put(setLoading('loadingPlanForTurnBackOn', true));
    const result = yield call(initStateWithOfferSaga);
    if (!result || !result.error) {
      yield call(
        Navigation.dispatch,
        CommonActions.navigate({name: 'choosePlanDuringTurnBackOn'}),
      );
    } else {
      HandleErrorService.showToastError(result.error);
    }
    yield put(setLoading('loadingPlanForTurnBackOn', false));
  } else {
    yield call(Navigation.dispatch, CommonActions.navigate({name: 'yourPlan'}));
  }
}

export function* saga(): Generator<IOEffect, void, any> {
  yield all([takeEvery('FETCH_DASHBOARD_DATA', fetchDashboardDataSaga)]);
  yield all([takeEvery('SELECT_SUBSCRIPTION', selectSubscriptionSaga)]);
  yield all([takeEvery('TOUCH_PLAN_FRAME', touchPlanFrameSaga)]);
}
